import { recordingRates, rehearsalRates } from '../data/sessions'
import type { RateRow } from '../types/sessions'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  }).format(value)

function RateTable({
  title,
  description,
  rates,
  className,
}: {
  title: string
  description: string
  rates: RateRow[]
  className: string
}) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-neutral-300 bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-900 ${className}`}
    >
      <div className="border-b border-neutral-200 bg-neutral-50 px-5 py-4 dark:border-neutral-700 dark:bg-neutral-800/50">
        <h3 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          {title}
        </h3>
        <p className="text-sm mt-1 text-neutral-600 dark:text-neutral-400">
          {description}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="text-sm min-w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-100/50 dark:border-neutral-700 dark:bg-neutral-800/30">
              <th className="text-xs px-5 py-3 text-left font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Session type
              </th>
              <th className="text-xs px-5 py-3 text-right font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Rate
              </th>
            </tr>
          </thead>
          <tbody>
            {rates.map((row, idx) => (
              <tr
                key={row.label}
                className={`transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800/40 ${idx % 2 === 0 ? 'bg-white dark:bg-neutral-900' : 'bg-neutral-50/50 dark:bg-neutral-800/20'} ${idx !== rates.length - 1 ? 'border-b border-neutral-100 dark:border-neutral-800' : ''} `}
              >
                <td className="px-5 py-3.5 font-medium text-neutral-800 dark:text-neutral-100">
                  {row.label}
                </td>
                <td className="px-5 py-3.5 text-right">
                  {row.promo && row.active ? (
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(row.promo)}
                      </span>
                      <span className="text-xs text-neutral-400 line-through dark:text-neutral-500">
                        {formatCurrency(row.regular)}
                      </span>
                    </div>
                  ) : (
                    <span className="font-medium text-neutral-700 dark:text-neutral-200">
                      {formatCurrency(row.regular)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function Rates() {
  return (
    <section
      id="rates"
      className="container-full cv-auto bg-neutral-100 py-10 dark:bg-neutral-950 sm:py-14"
    >
      <div className="container-responsive">
        {/* Header */}
        <div className="mb-8 max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-50">
            Rates
          </h2>
          <p className="text-base mt-3 leading-relaxed text-neutral-600 dark:text-neutral-400">
            Rates below are for time in the studio. Deposits and tax (GST) are
            calculated at booking and are not shown here. If you have a specific
            project in mind, book a free 30-minute consult and we can walk
            through options together.
          </p>
          {/* <div className="text-sm mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-2 font-medium text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-800">
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Promo rates apply to sessions scheduled before March 31, 2026
          </div> */}
        </div>

        {/* Hourly engineer rates */}
        <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="group relative overflow-hidden rounded-xl border border-neutral-300 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-neutral-700 dark:bg-neutral-900">
            <div className="absolute right-0 top-0 h-24 w-24 -translate-y-8 translate-x-8 rounded-full bg-neutral-100 dark:bg-neutral-800" />
            <div className="relative">
              <span className="text-xs inline-block rounded-full bg-neutral-100 px-2.5 py-0.5 font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">
                Hourly
              </span>
              <h3 className="text-lg mt-3 font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
                Senior Engineer
              </h3>
              <p className="text-sm mt-2 leading-relaxed text-neutral-600 dark:text-neutral-400">
                For independent artists and small production companies.
              </p>
              <p className="mt-4 text-3xl font-bold text-neutral-900 dark:text-neutral-50">
                {formatCurrency(110)}
                <span className="text-base ml-1 font-normal text-neutral-500 dark:text-neutral-400">
                  /hour
                </span>
              </p>
            </div>
          </div>

          <div className="bg-linear-to-br group relative overflow-hidden rounded-xl border border-amber-200 from-amber-50 to-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-amber-900/50 dark:from-amber-950/20 dark:to-neutral-900">
            <div className="absolute right-0 top-0 h-24 w-24 -translate-y-8 translate-x-8 rounded-full bg-amber-100/50 dark:bg-amber-900/20" />
            <div className="relative">
              <span className="text-xs inline-block rounded-full bg-amber-100 px-2.5 py-0.5 font-medium text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                Commercial
              </span>
              <h3 className="text-lg mt-3 font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
                ADR / VO Sessions
              </h3>
              <p className="text-sm mt-2 leading-relaxed text-neutral-600 dark:text-neutral-400">
                Dialogue, ADR, and voiceover sessions with full engineer
                support.
              </p>
              <p className="mt-4 text-3xl font-bold text-neutral-900 dark:text-neutral-50">
                {formatCurrency(250)}
                <span className="text-base ml-1 font-normal text-neutral-500 dark:text-neutral-400">
                  /hour
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Rate tables */}
        <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-5">
          <RateTable
            title="Recording Day Rates"
            description="Full-band recording sessions with an engineer for the duration of the booking."
            rates={recordingRates}
            className="col-span-1 md:col-span-3"
          />

          <RateTable
            title="Rehearsal Rates"
            description="Use the sound stage as a rehearsal room. An engineer will help you get set up and dialled in at the start of your booking."
            rates={rehearsalRates}
            className="col-span-1 md:col-span-2"
          />
        </div>

        {/* Footer note */}
        <p className="text-sm mt-6 text-neutral-500 dark:text-neutral-500">
          Promotional rates are subject to change. Final totals with deposits,
          GST, and any additional services (editing, Pro Tools support,
          overtime, etc.) are confirmed at booking.
        </p>
      </div>
    </section>
  )
}
